import { useEffect, useRef, useState } from 'react';

import { Delete02Icon } from '@hugeicons/core-free-icons';
import { MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import { getFetchBaseQueryErrorMessage } from 'helpers/api';
import {
  AnimatePresence,
  MotionConfig,
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
} from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button, Input } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';

import { messagePopUp } from 'api/app-slice';
import {
  billingApi,
  useDeleteBillingEntityOptionMutation,
  useDeleteBillingEntityOptionSetMutation,
  useGetBillingStructureQuery,
  useSaveBillingEntityOptionSetMutation,
  useUpdateBillingEntityAddressMutation,
  useUpdateBillingEntityMutation,
  useUpdateBillingStructureMutation,
} from 'api/billing-api';

import { billingDetailsStatus } from '../../billing-details';
import { BillingAddressForm } from '../BillingAddressForm/BillingAddressForm';
import { BillingEntitiesTable } from '../BillingEntitiesTable/BillingEntitiesTable';
import { BillingModeCards } from '../BillingModeCards/BillingModeCards';
import { CompanyBillingDetails } from '../CompanyBillingDetails/CompanyBillingDetails';
import { CustomFieldInclusion } from '../CustomFieldInclusion/CustomFieldInclusion';
import { EditableBillingEntityName } from '../EditableBillingEntityName/EditableBillingEntityName';

const SECTION_MODES = ['team', 'brand'];

const AnimatedHeight = ({ children }) => {
  const contentRef = useRef(null);
  const height = useMotionValue('auto');
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    let measured = false;
    const observer = new ResizeObserver(([entry]) => {
      const nextHeight = entry.target.offsetHeight;
      if (!measured || reduceMotion) {
        measured = true;
        height.set(nextHeight);
        return;
      }
      animate(height, nextHeight, { duration: 0.4, ease: 'easeInOut' });
    });
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [reduceMotion]);

  return (
    <motion.div style={{ height }} className="overflow-hidden">
      <div ref={contentRef} className="flow-root">
        {children}
      </div>
    </motion.div>
  );
};

const FadeSection = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } }}
    exit={{ opacity: 0, transition: { duration: 0.15, ease: 'easeOut' } }}>
    {children}
  </motion.div>
);

export const BillingStructure = () => {
  const { t } = useTranslation();
  const emptyEntityOptionSet = {
    name: t('company.tabs.billing.structure.defaultEntityLabel'),
    options: [{ name: '' }],
  };
  const { tab } = useParams();
  const {
    data: structure,
    isLoading,
    fulfilledTimeStamp,
    refetch,
  } = useGetBillingStructureQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const billingTabActive = useRef(tab === 'billing');

  useEffect(() => {
    const active = tab === 'billing';
    if (active && !billingTabActive.current) refetch();
    billingTabActive.current = active;
  }, [tab, refetch]);
  const dispatch = useDispatch();
  const [updateStructure, { isLoading: isSavingStructure }] = useUpdateBillingStructureMutation();
  const [saveEntityOptionSet, { isLoading: isSavingOptionSet }] =
    useSaveBillingEntityOptionSetMutation();
  const [deleteEntityOptionSet, { isLoading: isDeletingOptionSet }] =
    useDeleteBillingEntityOptionSetMutation();
  const [deleteEntityOption] = useDeleteBillingEntityOptionMutation();
  const [updateEntity] = useUpdateBillingEntityMutation();
  const [updateCompanyBillingDetails] = useUpdateBillingEntityAddressMutation();

  const [pendingMode, setPendingMode] = useState(null);
  const [entityOptionSetDraft, setEntityOptionSetDraft] = useState(emptyEntityOptionSet);
  const [entityToEdit, setEntityToEdit] = useState(null);
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState(false);
  const [error, setError] = useState('');
  const [showGroupErrors, setShowGroupErrors] = useState(false);
  const [newOptionIndex, setNewOptionIndex] = useState(null);
  const [modeSaveState, setModeSaveState] = useState(null);
  const [modeChangedByUser, setModeChangedByUser] = useState(false);
  const [highlight, setHighlight] = useState({ ids: [], company: false, token: 0 });
  const statusesBeforeCompanySave = useRef(null);

  const mode = pendingMode || structure?.billing_mode || 'company';

  useEffect(() => {
    if (pendingMode && pendingMode === structure?.billing_mode) {
      setPendingMode(null);
    }
  }, [structure?.billing_mode]);

  useEffect(() => {
    if (modeSaveState !== 'saved') return;
    const timeout = setTimeout(() => setModeSaveState(null), 1400);
    return () => clearTimeout(timeout);
  }, [modeSaveState]);

  const optionSetFromServer = optionSet =>
    optionSet
      ? {
          name: optionSet.name || '',
          options: (optionSet.options || []).map(option => ({
            id: option.id,
            name: option.name || '',
          })),
        }
      : emptyEntityOptionSet;
  const optionSetKey = optionSet =>
    JSON.stringify({
      name: (optionSet?.name || '').trim(),
      options: (optionSet?.options || []).map(option => ({
        id: option.id || null,
        name: (option.name || '').trim(),
      })),
    });
  const savedOptionSetKey = optionSetKey(optionSetFromServer(structure?.entity_option_set));
  const draftRef = useRef(entityOptionSetDraft);
  const savedOptionSetKeyRef = useRef(null);
  draftRef.current = entityOptionSetDraft;

  useEffect(() => {
    const previousKey = savedOptionSetKeyRef.current;
    savedOptionSetKeyRef.current = savedOptionSetKey;
    const server = optionSetFromServer(structure?.entity_option_set);
    const draft = draftRef.current;
    const draftMatchesPrevious = previousKey === null || optionSetKey(draft) === previousKey;
    const savedNamesMatch =
      (draft?.name || '').trim() === (server?.name || '').trim() &&
      (draft?.options || []).length === (server?.options || []).length &&
      (draft?.options || []).every(
        (option, index) =>
          (option.name || '').trim() === (server.options[index]?.name || '').trim(),
      );
    if (!draftMatchesPrevious && !savedNamesMatch) return;

    setShowGroupErrors(false);
    setNewOptionIndex(null);
    setEntityOptionSetDraft(server);
  }, [savedOptionSetKey]);

  const save = async action => {
    setError('');
    const unsavedEntityMode =
      pendingMode === 'entity' && !structure?.entity_option_set?.options?.length;
    try {
      await action();
      if (!unsavedEntityMode) setPendingMode(null);
      return true;
    } catch (saveError) {
      setError(
        getFetchBaseQueryErrorMessage(saveError) || t('company.tabs.billing.structure.saveError'),
      );
      return false;
    }
  };

  const highlightChanges = (ids, company = false) =>
    setHighlight(current => ({ ids, company, token: current.token + 1 }));

  const chooseMode = async nextMode => {
    setModeChangedByUser(true);
    setHighlight(current => ({ ...current, ids: [], company: false }));

    if (nextMode === 'entity' && !structure?.entity_option_set?.options?.length) {
      setPendingMode('entity');
      return;
    }

    setError('');
    setPendingMode(nextMode);
    setModeSaveState('saving');
    try {
      await updateStructure({ billing_mode: nextMode }).unwrap();
      setModeSaveState('saved');
    } catch (saveError) {
      setPendingMode(null);
      setModeSaveState(null);
      setError(
        getFetchBaseQueryErrorMessage(saveError) || t('company.tabs.billing.structure.saveError'),
      );
    }
  };

  const saveCustomFields = (include, fields) =>
    save(() =>
      updateStructure({
        include_custom_fields: include,
        custom_fields: fields.map(field => ({
          id: field.id,
          include_in_billing: field.include_in_billing,
        })),
      }).unwrap(),
    );

  const saveOptionSet = async () => {
    const missingGroupName = entityOptionSetDraft.options.some(
      option => (option.name || '').trim() === '',
    );
    if (missingGroupName) {
      setShowGroupErrors(true);
      return;
    }

    setShowGroupErrors(false);

    let savedStructure = null;
    const saved = await save(async () => {
      savedStructure =
        entityOptionSetDraft.options.length === 0
          ? structure.entity_option_set
            ? await deleteEntityOptionSet().unwrap()
            : null
          : await saveEntityOptionSet(entityOptionSetDraft).unwrap();
    });

    if (savedStructure) {
      dispatch(
        billingApi.util.updateQueryData('getBillingStructure', undefined, () => savedStructure),
      );
      setEntityOptionSetDraft(optionSetFromServer(savedStructure.entity_option_set));
    }

    if (saved) {
      dispatch(
        messagePopUp({
          text: t('company.tabs.billing.structure.entitiesSaved'),
          state: MESSAGE_STATE_SUCCESS,
          hide: true,
        }),
      );
    }
  };

  const matchesMode = entity => {
    if (mode === 'team') return !!entity.team_id;
    if (mode === 'brand') return !!entity.brand_id;
    return false;
  };
  const sections = (structure?.entities || []).filter(
    entity => !entity.default && !entity.deleted && matchesMode(entity),
  );

  const savedOptionSet = structure?.entity_option_set;
  const groupsChanged =
    (entityOptionSetDraft.name || '').trim() !== (savedOptionSet?.name || '').trim() ||
    entityOptionSetDraft.options.length !== (savedOptionSet?.options || []).length ||
    entityOptionSetDraft.options.some(
      (option, index) =>
        (option.name || '').trim() !== (savedOptionSet?.options?.[index]?.name || '').trim(),
    );
  const showEntitySaveBar = groupsChanged && entityOptionSetDraft.options.length > 0;
  const savedOptionNames = Object.fromEntries(
    (savedOptionSet?.options || []).map(option => [option.id, (option.name || '').trim()]),
  );
  const unsavedChangeCount =
    entityOptionSetDraft.options.filter(
      option => !option.id || savedOptionNames[option.id] !== (option.name || '').trim(),
    ).length +
    (savedOptionSet &&
    (entityOptionSetDraft.name || '').trim() !== (savedOptionSet.name || '').trim()
      ? 1
      : 0);
  const entityLabel =
    (entityOptionSetDraft.name || '').trim() ||
    t('company.tabs.billing.structure.fallbackEntityLabel');

  const resetEntityOptionSetDraft = () => {
    setShowGroupErrors(false);
    setNewOptionIndex(null);
    setEntityOptionSetDraft(optionSetFromServer(structure?.entity_option_set));
  };

  const addOption = () => {
    setNewOptionIndex(entityOptionSetDraft.options.length);
    setEntityOptionSetDraft({
      ...entityOptionSetDraft,
      options: [...entityOptionSetDraft.options, { name: '' }],
    });
  };

  const removeOption = async index => {
    const removed = entityOptionSetDraft.options[index];
    const options = entityOptionSetDraft.options.filter((_, optionIndex) => optionIndex !== index);
    setEntityOptionSetDraft({ ...entityOptionSetDraft, options });
    if (!removed?.id) return;

    const saved = await save(() => deleteEntityOption(removed.id).unwrap());
    if (!saved) {
      setEntityOptionSetDraft(current => ({
        ...current,
        options: [...current.options.slice(0, index), removed, ...current.options.slice(index)],
      }));
    }
  };

  const companyEntity = (structure?.entities || []).find(
    entity => entity.default && !entity.deleted,
  );

  const entityForOption = option =>
    option.id
      ? (structure?.entities || []).find(
          entity =>
            !entity.default &&
            !entity.deleted &&
            Number(entity.billing_entity_option_id) === Number(option.id),
        )
      : null;

  const visibleEntities =
    mode === 'entity'
      ? entityOptionSetDraft.options.map(entityForOption).filter(Boolean)
      : sections;

  useEffect(() => {
    const before = statusesBeforeCompanySave.current;
    if (!before) return;

    statusesBeforeCompanySave.current = null;
    const changedIds = visibleEntities
      .filter(entity => before[entity.id] !== billingDetailsStatus(entity, companyEntity))
      .map(entity => entity.id);
    highlightChanges(changedIds, true);
  }, [fulfilledTimeStamp]);

  const saveCompanyBillingDetails = values =>
    save(async () => {
      statusesBeforeCompanySave.current = Object.fromEntries(
        visibleEntities.map(entity => [entity.id, billingDetailsStatus(entity, companyEntity)]),
      );
      try {
        await updateCompanyBillingDetails(values).unwrap();
      } catch (saveError) {
        statusesBeforeCompanySave.current = null;
        throw saveError;
      }
      setShowCompanyDetailsModal(false);
    });

  const saveEntityBillingDetails = values =>
    save(async () => {
      await updateEntity({ id: entityToEdit.id, ...values }).unwrap();
      highlightChanges([entityToEdit.id]);
      setEntityToEdit(null);
    });

  const updateOptionName = (index, name) => {
    const options = [...entityOptionSetDraft.options];
    options[index] = { ...options[index], name };
    setEntityOptionSetDraft({ ...entityOptionSetDraft, options });
  };

  if (isLoading || !structure) {
    return null;
  }

  const sectionKey = SECTION_MODES.includes(mode) ? `company.tabs.billing.sections.${mode}` : null;

  return (
    <MotionConfig reducedMotion="user">
      <div className="pb-3">
        <div className="flex flex-col-reverse gap-10 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1 lg:pt-[17px]">
            <h2 className="m-0 mb-1 text-3xl font-semibold text-text-primary">
              {t('company.tabs.billing.structure.title')}
            </h2>
            <p className="m-0 text-xl text-text-secondary">
              {t('company.tabs.billing.structure.description')}
            </p>
            {error && <p className="t-small text-error mt-3">{error}</p>}
            <div className="mt-6">
              <BillingModeCards
                modes={structure.available_modes}
                value={mode}
                onChange={chooseMode}
                disabled={isSavingStructure}
                saveState={modeSaveState}
              />
            </div>

            <AnimatedHeight>
              <AnimatePresence initial={false} mode="wait">
                {sectionKey && sections.length > 0 && (
                  <FadeSection key={mode}>
                    <section className="mt-14">
                      <h3 className="m-0 mb-1 text-3xl font-semibold text-text-primary">
                        {t(`${sectionKey}.title`)}
                      </h3>
                      <p className="m-0 text-xl text-text-secondary mb-6">
                        {t(`${sectionKey}.description`)}
                      </p>
                      <BillingEntitiesTable
                        label={t(`${sectionKey}.label`)}
                        companyEntity={companyEntity}
                        onEdit={setEntityToEdit}
                        highlight={highlight}
                        animateRowsIn={modeChangedByUser}
                        rows={sections.map(entity => ({
                          key: entity.id,
                          name: entity.name,
                          entity,
                        }))}
                      />
                    </section>
                  </FadeSection>
                )}

                {mode === 'entity' && (
                  <FadeSection key="entity">
                    <section className="mt-14">
                      <h3 className="m-0 mb-1 text-3xl font-semibold text-text-primary">
                        {t('company.tabs.billing.sections.entity.title')}
                      </h3>
                      <p className="m-0 text-xl text-text-secondary mb-6">
                        {t('company.tabs.billing.sections.entity.description')}
                      </p>
                      <BillingEntitiesTable
                        label={
                          <EditableBillingEntityName
                            value={entityOptionSetDraft.name}
                            onChange={name =>
                              setEntityOptionSetDraft({ ...entityOptionSetDraft, name })
                            }
                          />
                        }
                        companyEntity={companyEntity}
                        onEdit={setEntityToEdit}
                        highlight={highlight}
                        animateRowsIn={modeChangedByUser}
                        rows={entityOptionSetDraft.options.map((option, index) => ({
                          key: option.id ? `option-${option.id}` : `new-${index}`,
                          entity: entityForOption(option),
                          name: (
                            <Input
                              name={`option_${index}`}
                              placeholder={t('company.tabs.billing.entities.namePlaceholder', {
                                label: entityLabel,
                              })}
                              error={
                                showGroupErrors && (option.name || '').trim() === ''
                                  ? t('company.tabs.billing.entities.nameRequired')
                                  : ''
                              }
                              inputProps={{
                                value: option.name,
                                autoFocus: index === newOptionIndex,
                                onChange: event => updateOptionName(index, event.target.value),
                              }}
                            />
                          ),
                          extraAction: (
                            <IconButton
                              icon={Delete02Icon}
                              prefix="hugeicons"
                              type="delete"
                              tip={t('company.tabs.billing.entities.remove')}
                              onClick={() => removeOption(index)}
                            />
                          ),
                        }))}
                        footer={
                          <div className="px-5 py-3">
                            <Button type="link" small icon="plus" onClick={addOption}>
                              {t('company.tabs.billing.entities.add', {
                                label: entityLabel.toLowerCase(),
                              })}
                            </Button>
                          </div>
                        }
                      />
                      <AnimatePresence initial={false}>
                        {showEntitySaveBar && (
                          <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                            className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border-primary bg-white px-5 py-3">
                            <span className="text-xl text-text-secondary">
                              {t('company.tabs.billing.entities.unsavedChanges', {
                                count: unsavedChangeCount,
                              })}
                            </span>
                            <div className="flex items-center gap-3">
                              <Button type="secondary" small onClick={resetEntityOptionSetDraft}>
                                {t('company.tabs.billing.entities.discard')}
                              </Button>
                              <Button
                                small
                                disabled={isSavingOptionSet || isDeletingOptionSet}
                                onClick={saveOptionSet}>
                                {t('company.tabs.billing.entities.saveChanges')}
                              </Button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </section>
                  </FadeSection>
                )}
              </AnimatePresence>
            </AnimatedHeight>

            <CustomFieldInclusion
              structure={structure}
              disabled={isSavingStructure}
              onChange={saveCustomFields}
            />
          </div>

          <CompanyBillingDetails
            entity={companyEntity}
            onEdit={() => setShowCompanyDetailsModal(true)}
            highlightToken={highlight.company ? highlight.token : 0}
          />
        </div>

        <AnimatedModal
          title={t('company.tabs.billing.modals.companyTitle')}
          small
          visible={showCompanyDetailsModal}
          onClose={() => setShowCompanyDetailsModal(false)}>
          <BillingAddressForm
            billingEntity={companyEntity}
            onClose={() => setShowCompanyDetailsModal(false)}
            handleUpdate={saveCompanyBillingDetails}
          />
        </AnimatedModal>

        <AnimatedModal
          title={
            entityToEdit
              ? t('company.tabs.billing.modals.entityTitle', { name: entityToEdit.name })
              : t('company.tabs.billing.modals.entityTitleFallback')
          }
          small
          visible={!!entityToEdit}
          onClose={() => setEntityToEdit(null)}>
          {entityToEdit && (
            <BillingAddressForm
              billingEntity={entityToEdit}
              companyEntity={companyEntity}
              onClose={() => setEntityToEdit(null)}
              handleUpdate={saveEntityBillingDetails}
            />
          )}
        </AnimatedModal>
      </div>
    </MotionConfig>
  );
};
